import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/tracking";

/**
 * Floating right-edge "page sections" rail.
 *
 * Props:
 * - sections: [{ id: string, label: string }]  // id = the data-testid of the target section
 * - service: string                            // tracking source label
 *
 * Behaviour:
 * - Desktop-only (xl: breakpoint)
 * - Hidden until the user scrolls past 400px
 * - Active dot highlights the section currently in view (top half of viewport)
 * - Click → smooth-scroll to target section
 */
export default function SectionRail({ sections, service }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 400);
      // Find the section whose top is closest to (but above) the viewport's 35% line
      const probe = window.innerHeight * 0.35;
      let candidate = null;
      let candidateTop = -Infinity;
      for (const s of sections) {
        const el = document.querySelector(`[data-testid="${s.id}"]`);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= probe && top > candidateTop) {
          candidate = s.id;
          candidateTop = top;
        }
      }
      if (candidate) setActiveId(candidate);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sections]);

  const jump = (id, label) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    trackEvent("section_rail_click", { section: id, label, service });
  };

  return (
    <nav
      aria-label="Page sections"
      data-testid="section-rail"
      className={`hidden xl:flex fixed top-1/2 right-5 z-30 -translate-y-1/2 flex-col gap-3 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {sections.map((s) => {
        const active = s.id === activeId;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => jump(s.id, s.label)}
            data-testid={`section-rail-${s.id}`}
            aria-current={active ? "true" : "false"}
            aria-label={`Jump to ${s.label}`}
            className="group relative flex items-center gap-3 outline-none"
          >
            {/* Floating label */}
            <span
              className={`pointer-events-none rounded-full bg-zinc-900 text-white text-[11px] font-bold tracking-wide px-3 py-1 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] transition-all whitespace-nowrap ${
                active
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0"
              }`}
            >
              {s.label}
            </span>
            {/* Dot / pill */}
            <span
              className={`block rounded-full transition-all ${
                active
                  ? "h-2 w-8 bg-zinc-900"
                  : "h-2 w-2 bg-zinc-300 group-hover:bg-zinc-500 group-focus-visible:bg-zinc-500"
              }`}
            />
          </button>
        );
      })}
    </nav>
  );
}
